import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { StatusProvider } from "@/context/StatusContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import AdminLayout from "@/components/AdminLayout";
import Login from "@/pages/Login";
import InviteAccept from "@/pages/InviteAccept";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Tickets from "@/pages/Tickets";
import NewTicket from "@/pages/NewTicket";
import TicketDetail from "@/pages/TicketDetail";
import Projects from "@/pages/Projects";
import Organizations from "@/pages/Organizations";
import OrganizationDetail from "@/pages/OrganizationDetail";
import ProjectDetail from "@/pages/ProjectDetail";
import UserDetail from "@/pages/UserDetail";
import UserFormPage from "@/pages/UserFormPage";
import Users from "@/pages/Users";
import Reports from "@/pages/Reports";
import AuditLog from "@/pages/AuditLog";
import Settings from "@/pages/Settings";
import Overview from "@/pages/admin/Overview";
import TenantDetail from "@/pages/admin/TenantDetail";
import Plans from "@/pages/admin/Plans";
import Updates from "@/pages/admin/Updates";

function App() {
  return (
    <AuthProvider>
      <StatusProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/login" element={<Login />} />
          {/* Public sign-up is invite-only now */}
          <Route path="/register" element={<Navigate to="/login" replace />} />
          <Route path="/invite/:token" element={<InviteAccept />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout><Dashboard /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tickets"
            element={
              <ProtectedRoute>
                <Layout><Tickets /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tickets/new"
            element={
              <ProtectedRoute>
                <Layout><NewTicket /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tickets/:id"
            element={
              <ProtectedRoute>
                <Layout><TicketDetail /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects"
            element={
              <ProtectedRoute>
                <Layout><Projects /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/organizations"
            element={
              <ProtectedRoute roles={["super_admin", "project_manager"]}>
                <Layout><Organizations /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/organizations/:id"
            element={
              <ProtectedRoute>
                <Layout><OrganizationDetail /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:id"
            element={
              <ProtectedRoute>
                <Layout><ProjectDetail /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute roles={["super_admin", "project_manager", "client_admin"]}>
                <Layout><Users /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/new"
            element={
              <ProtectedRoute roles={["super_admin", "project_manager", "client_admin"]}>
                <Layout><UserFormPage /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/:id/edit"
            element={
              <ProtectedRoute roles={["super_admin", "project_manager", "client_admin"]}>
                <Layout><UserFormPage /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/:id"
            element={
              <ProtectedRoute>
                <Layout><UserDetail /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute roles={["super_admin", "project_manager"]}>
                <Layout><Reports /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/audit"
            element={
              <ProtectedRoute roles={["super_admin"]}>
                <Layout><AuditLog /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Layout><Settings /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/masters"
            element={<Navigate to="/settings" replace />}
          />

          <Route
            path="/admin"
            element={
              <ProtectedRoute platform>
                <AdminLayout><Overview /></AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/plans"
            element={
              <ProtectedRoute platform>
                <AdminLayout><Plans /></AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/updates"
            element={
              <ProtectedRoute platform>
                <AdminLayout><Updates /></AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/tenants/:id"
            element={
              <ProtectedRoute platform>
                <AdminLayout><TenantDetail /></AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      </StatusProvider>
    </AuthProvider>
  );
}

export default App;
